
import { Component, Prop } from 'vue-property-decorator';
import moment from 'moment';
import SelectButton from 'primevue/selectbutton';
import Button from 'primevue/button';
import LineChartFilterable from '@/components/reports-v3/components/filterables/LineChartFilterable.vue';
import PieChartFilterable from '@/components/reports-v3/components/filterables/PieChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
/* tslint:disable-next-line:max-line-length */
import KimChokChartOptions, { ChartOptions } from '@/components/reports-v3/components/codedWidgets/custom/kim-chok-sdn-bhd/KimChokChartOptions.vue';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import { FilterableCardName, PermissionsGroup, ValueText } from '@/store/models.def';
import { addComma } from '@/util/number';
import {
    getTableName,
    getSqlColumnRef,
    isComputedProperty,
    sqlExtractYear,
    sqlExtractMonth,
    sqlExtractDay,
    COL,
} from '@/worker/db/sqlHelpers';

/** The single card backing all charts: IV / CS / DN / CN keyed by "DOCTYPE". */
const CARD: FilterableCardName = 'diySalesIvCsDnCn';

/** Group columns in `diySalesIvCsDnCn` (V1-2). Referenced raw (UPPERCASE). */
const LOCATION_COL = '"LOCATION"';
const UDF_GROUP_COL = '"UDF_GROUP"';

interface IncludeOpts {
    invoice: boolean;
    cash: boolean;
    credit: boolean;
    debit: boolean;
}

/** Per entity+period raw buckets (before the include-checkboxes are applied). */
interface Cell {
    iv: number;
    cs: number;
    dn: number;
    cn: number;
}

interface RawSeries {
    labels: string[];
    keys: string[];
    entities: Array<{ code: string; name: string; byPeriod: { [k: string]: Cell } }>;
}

/** Short number: 1234 -> "1.23k", 1_200_000 -> "1.2m", 2_500_000_000 -> "2.5b". */
function shortNumber(v: number): string {
    const abs = Math.abs(v);
    const sign = v < 0 ? '-' : '';
    if (abs >= 1e9) {
        return sign + trimDec(abs / 1e9) + 'b';
    }
    if (abs >= 1e6) {
        return sign + trimDec(abs / 1e6) + 'm';
    }
    if (abs >= 1e3) {
        return sign + trimDec(abs / 1e3) + 'k';
    }
    return sign + String(Math.round(abs));
}

function trimDec(n: number): string {
    return parseFloat(n.toFixed(2)).toString();
}

/** ECharts coloured-dot span (matches TooltipFormatter._colorSpan). */
function colorDot(color: string): string {
    return '<span style="display:inline-block;margin-right:5px;border-radius:10px;'
        + 'width:9px;height:9px;background-color:' + color + '"></span>';
}

/**
 * Axis tooltip: each series (colour + name + value, zeros hidden, sorted high→low)
 * for the hovered period, followed by a bold Total = sum of all series at that point.
 */
function lineTooltipWithTotal(paramsRaw: any): string {
    const all = (paramsRaw as any[]) || [];
    if (all.length === 0) {
        return '';
    }
    const dec = gdbx.numDecimal;
    const rows = all.filter((p) => Number(p.value) !== 0);
    rows.sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));

    let total = 0;
    let rez = all[0].axisValue + '<br />';
    rows.forEach((item, i) => {
        const value = Number(item.value) || 0;
        total += value;
        if (i > 0) {
            rez += '<br />';
        }
        rez += colorDot(item.color) + ' ' + item.seriesName + ': ' + addComma(value, dec, true);
    });
    rez += '<br /><b>Total: ' + addComma(total, dec, true) + '</b>';
    return rez;
}

/**
 * dataView ("Chart data" popup) table for the pie: Location / Value / % as three
 * separate columns, so the share of total sits in its own column. Percentage is
 * over the shown slices (matches the pie's Total and slice percentages).
 */
function pieDataViewWithPercent(opt: any): string {
    const data: any[] = (opt.series && opt.series[0] && opt.series[0].data) || [];
    const dec = gdbx.numDecimal;
    const sym = gdbx.currencySymbol;
    const valueOf = (d: any) => (typeof d === 'object' ? Number(d.value) : Number(d)) || 0;
    const total = data.reduce((sum, d) => sum + valueOf(d), 0);

    let rows = '';
    for (const d of data) {
        const name = typeof d === 'object' ? d.name : '';
        const value = valueOf(d);
        const pct = total > 0 ? (value / total) * 100 : 0;
        rows += '<tr>'
            + '<td style="padding: 0 10px">' + name + '</td>'
            + '<td>' + sym + ' ' + addComma(value, dec, true) + '</td>'
            + '<td>' + pct.toFixed(1) + '%</td>'
            + '</tr>';
    }
    return '<div contenteditable="true">'
        + '<table class="table table-bordered table-striped table-hover" style="text-align:center;">'
        + '<tbody><tr><th>Location</th><th>Value</th><th>%</th></tr>'
        + rows
        + '</tbody></table></div>';
}

/**
 * KIM CHOK SDN BHD (account dgHU51v0NnYfbeF26Knr) custom dashboard.
 *
 * Reads the purpose-built `diySalesIvCsDnCn` card (dashboard group), which holds
 * IV, CS, DN and CN in ONE table keyed by "DOCTYPE" — CN rows stored POSITIVE.
 * Net per line = (Sales Invoice ? IV) + (Cash Sales ? CS) + (Debit Note ? DN)
 *                - (Credit Note ? CN). Amounts are LOCAL ("AMOUNT" = SUM(LOCALAMOUNT),
 * before tax); the Tax checkbox adds the local "TAX" column.
 *
 * The charts (Location line, Group line, Location pie) are driven by the GLOBAL
 * page filters (Location / Stock Group / Group / Customers), which combine with AND:
 *   - LOCATION   = detail location (1 per branch), a system field with code + name.
 *   - STOCKGROUP = the system stock group (filter only, no chart; options from gdbx).
 *   - UDF_GROUP  = a DIY field grouping above stock-group level; self-describing
 *                  (value IS the label), so its filter options come from the table's
 *                  own DISTINCT values, not from gdbx.
 * All charts SHARE the doc-type / Tax / Monthly-7Days / Show Values controls, so
 * toggling one applies to every chart.
 */
@Component({
    components: {
        SelectButton,
        Button,
        LineChartFilterable,
        PieChartFilterable,
        KimChokChartOptions,
    },
})
export default class KimChokSdnBhdDashboard extends FilterWidget {
    /** `diySalesIvCsDnCn` lives in the dashboard group, like the other diy cards. */
    public get permissionIds(): PermissionsGroup[] {
        return ['dashboard'];
    }

    public get currencySymbol() {
        return gdbx.currencySymbol;
    }

    public get tooltipFormatter() {
        return lineTooltipWithTotal;
    }

    /** location code -> location name (detail location = branch) */
    public get locationNameMap(): { [code: string]: string } {
        const map: { [code: string]: string } = {};
        for (const l of gdbx.allLocations) {
            map[l.value] = l.text;
        }
        return map;
    }

    /** stock group code -> stock group description (system field, from gdbx). */
    public get stockGroupNameMap(): { [code: string]: string } {
        return gdbx.stockGroupNames;
    }

    // ---- Series / axes derived from the raw buckets + the checkboxes (no re-query) ----
    public get xAxisDataLocation(): string[] {
        return this.locationRaw ? this.locationRaw.labels : [];
    }

    public get xAxisDataStock(): string[] {
        return this.stockRaw ? this.stockRaw.labels : [];
    }

    public get xAxisDataUdf(): string[] {
        return this.udfRaw ? this.udfRaw.labels : [];
    }

    public get includeOpts(): IncludeOpts {
        const { invoice, cash, credit, debit } = this.options;
        return { invoice, cash, credit, debit };
    }

    /**
     * Caption under the chart title. The document-type checkboxes now live
     * behind the Options button, so spell out what the lines actually add up
     * to — otherwise a chart with Credit Note on is indistinguishable from one
     * without it.
     */
    public get optionSummary(): string {
        const { invoice, cash, credit, debit, includeTax } = this.options;
        const added: string[] = [];
        if (invoice) {
            added.push('Sales Invoice');
        }
        if (cash) {
            added.push('Cash Sales');
        }
        if (debit) {
            added.push('Debit Note');
        }
        let summary = added.join(' + ');
        if (credit) {
            summary = summary ? `${summary} - Credit Note` : '- Credit Note';
        }
        if (!summary) {
            return 'No document type selected';
        }
        return includeTax ? `${summary} (incl. Tax)` : summary;
    }

    public get locationSeries(): any[] {
        return this._seriesFromRaw(this.locationRaw, this.includeOpts, this.options.showValues);
    }

    public get stockGroupSeries(): any[] {
        return this._seriesFromRaw(this.stockRaw, this.includeOpts, this.options.showValues);
    }

    public get udfGroupSeries(): any[] {
        return this._seriesFromRaw(this.udfRaw, this.includeOpts, this.options.showValues);
    }

    /**
     * Pie slices: net sales per location over the shared window (`view`),
     * combined with the shared doctype checkboxes. Only positive contributions are
     * shown (a pie of proportions), largest slice first.
     */
    public get locationPieData(): Array<{ name: string; value: number }> {
        if (!this.locationTotalsRaw) {
            return [];
        }
        return this.locationTotalsRaw
            .map((e) => ({ name: e.name, value: this._combine(e.cell, this.includeOpts) }))
            .filter((d) => d.value > 0)
            .sort((a, b) => b.value - a.value);
    }

    /**
     * Pie slice labels. By default show only the location name (clean); when
     * "Show Values" is ticked, show name + amount + share of total, e.g.
     * "BRANCH" then "RM 1,234 (12.3%)". The two lines use rich-text tags (loc /
     * val) so pieLabelRich can colour the amount line differently from the name.
     */
    public get pieLabelFormatter(): any {
        if (!this.options.showValues) {
            return '{b}';
        }
        const sym = this.currencySymbol;
        const dec = gdbx.numDecimal;
        return (p: any) =>
            `{loc|${p.name}}\n{val|${sym} ${addComma(Number(p.value), dec, true)} (${p.percent}%)}`;
    }

    /** Rich-text styles for the pie labels: name (loc) vs amount/percent (val). */
    public pieLabelRich: any = {
        loc: { fontWeight: 'bold' },
        val: { color: '#8c8c8c', fontSize: 11 },
    };

    /** "Chart data" popup for the pie — Location / Value / % in separate columns. */
    public get pieDataViewFormatter() {
        return pieDataViewWithPercent;
    }

    /**
     * The pie's date window. It shares the line charts' Date Range / 7 Days
     * toggle (`view`), so this is just the line charts' display range.
     */
    public get pieDisplayRange(): [number, number] {
        return this.displayRange(this.view);
    }

    public get expensiveHook() {
        // DB-fetch inputs — the doctype checkboxes / Show Values recombine reactively,
        // but view + Tax change the query (date window / amount expression) -> re-fetch.
        const {
            selectedDateRange, selectedLocations, selectedStockGroups, selectedUdfGroups,
            selectedCustomers, udfGroups, view,
        } = this;
        return JSON.stringify([
            selectedDateRange, selectedLocations, selectedStockGroups, selectedUdfGroups,
            selectedCustomers, udfGroups, view, this.options.includeTax,
        ]);
    }
    public filterIds: string[] = ['date', 'location', 'stockGroup', 'udfGroup', 'customers'];

    // Global-filter selections. selectedCustomers + selectedDateRange are inherited
    // from FilterWidget; Location, Stock Group and Group are not, so declare them here.
    @Prop({ default: () => [] }) public selectedLocations!: string[];
    @Prop({ default: () => [] }) public selectedStockGroups!: string[];
    @Prop({ default: () => [] }) public selectedUdfGroups!: string[];
    // Every distinct UDF_GROUP value (the filter's full option set). UDF_GROUP is a
    // DIY field with no gdbx source, so the page queries it and passes it down; the
    // widget needs the total to tell "all selected" from "some selected".
    @Prop({ default: () => [] }) public udfGroups!: string[];

    public viewOptions: ValueText[] = [
        // 'month' keeps monthly granularity under the hood; label reads "Date Range".
        { value: 'month', text: 'Date Range' },
        { value: '7days', text: '7 Days' },
    ];

    // 8 distinct line colours (palette cycles if more entities are selected)
    public palette: string[] = [
        GobiColor.COLOR1,
        GobiColor.BLUE,
        GobiColor.DEEP_TEAL,
        GobiColor.DARK_TEAL,
        GobiColor.ORANGE,
        GobiColor.PINK,
        GobiColor.RED,
        GobiColor.COLOR7,
    ];

    // ---- Shared controls (apply to ALL charts, pie included) ----
    // Date Range / 7 Days toggle shared by every chart, the pie included.
    public view: 'month' | '7days' = 'month';
    public options: ChartOptions = {
        // KIM CHOK's transactions are mostly Cash Sales, so default to CS (not IV).
        // Credit Note is on by default too, so the lines show net of returns.
        invoice: false,
        cash: true,
        credit: true,
        debit: false,
        includeTax: false,
        showValues: false,
    };

    // ---- Raw query buckets (checkbox-independent) ----
    public locationRaw: RawSeries | null = null;
    public stockRaw: RawSeries | null = null;
    public udfRaw: RawSeries | null = null;
    // Per-location totals over the full global range, for the comparison pie.
    public locationTotalsRaw: Array<{ code: string; name: string; cell: Cell }> | null = null;

    public async expensiveCalc() {
        this.generateLoadingText(0.2, 0.4);
        const jobId = this.currentJobId;
        const queryHead = await this._ensureTablesLoaded([CARD]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.4, 0.6);

        const locationRaw = await this._fetchRaw(
            queryHead, LOCATION_COL, this.selectedLocations, this.locationNameMap,
            this.view, this.options.includeTax,
        );
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.6, 0.7);

        const stockRaw = await this._fetchRaw(
            queryHead, COL.STOCKGROUP, this.selectedStockGroups, this.stockGroupNameMap,
            this.view, this.options.includeTax,
        );
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.7, 0.8);

        // UDF_GROUP is self-describing (value = label), so pass an empty name map:
        // _fetchRaw falls back to the code, which is exactly the group name.
        const udfRaw = await this._fetchRaw(
            queryHead, UDF_GROUP_COL, this.selectedUdfGroups, {},
            this.view, this.options.includeTax,
        );
        this.checkInterrupted(jobId);

        // Pie = per-location totals over the SAME window as the location line
        // (the shared Date Range / 7 Days toggle), so just sum that line's periods
        // — no separate query.
        const locationTotalsRaw: Array<{ code: string; name: string; cell: Cell }> =
            locationRaw.entities.map((e) => ({
                code: e.code,
                name: e.name,
                cell: this._sumCells(e.byPeriod),
            }));

        this.locationRaw = locationRaw;
        this.stockRaw = stockRaw;
        this.udfRaw = udfRaw;
        this.locationTotalsRaw = locationTotalsRaw;

        this.generateLoadingText(1);
        this.saveHistory('locationRaw', 'stockRaw', 'udfRaw', 'locationTotalsRaw');
    }

    /**
     * The date range shown in the chart header. In 7-Days view it reflects the
     * actual 7-day window ("Date To" - 6 ... "Date To"), and this is also the
     * DB query window; in Monthly view it's the full selected range.
     */
    public displayRange(viewMode: 'month' | '7days'): [number, number] {
        const [from, to] = this.selectedDateRange;
        if (viewMode === '7days') {
            return [
                moment(to).subtract(6, 'day').startOf('day').valueOf(),
                moment(to).endOf('day').valueOf(),
            ];
        }
        return [from, to];
    }

    public exportCsv(kind: 'location' | 'stock' | 'group') {
        const labels = kind === 'location'
            ? this.xAxisDataLocation
            : kind === 'stock'
                ? this.xAxisDataStock
                : this.xAxisDataUdf;
        const series = kind === 'location'
            ? this.locationSeries
            : kind === 'stock'
                ? this.stockGroupSeries
                : this.udfGroupSeries;
        const title = kind === 'location'
            ? 'Sales Comparison By Location'
            : kind === 'stock'
                ? 'Sales Comparison By Stock Group'
                : 'Sales Comparison By Category Group';

        const [from, to] = this.displayRange(this.view);
        const range = `${moment(from).format('DD MMM YY')} - ${moment(to).format('DD MMM YY')}`;

        const header = ['Period', ...series.map((s) => s.name)];
        const rows = labels.map((lbl, i) => [
            lbl,
            ...series.map((s) => s.data[i]),
        ]);
        const csv = [header, ...rows]
            .map((r) => r.map((cell) => this._csvCell(cell)).join(','))
            .join('\r\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `KIM CHOK - ${title} (${range}).csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }

    /** Export the pie (Location / Value / % over the pie's window) as CSV. */
    public exportPieCsv() {
        const rows = this.locationPieData;
        const total = rows.reduce((sum, r) => sum + r.value, 0);
        const [from, to] = this.pieDisplayRange;
        const range = `${moment(from).format('DD MMM YY')} - ${moment(to).format('DD MMM YY')}`;

        const header = ['Location', 'Value', '%'];
        const body = rows.map((r) => [
            r.name,
            r.value,
            total > 0 ? (r.value / total * 100).toFixed(1) : '0',
        ]);
        const csv = [header, ...body]
            .map((r) => r.map((cell) => this._csvCell(cell)).join(','))
            .join('\r\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `KIM CHOK - Sales Breakdown By Location (${range}).csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }

    /**
     * WHERE clause shared by every query on `diySalesIvCsDnCn`: the date window
     * (BETWEEN start..end) plus the global Detail Location / Group / Customer
     * filters, combined with AND.
     *
     * Returns null when a filter HAS options but the user selected none of them —
     * the AND intersection is then empty, i.e. no data (rather than silently
     * dropping the constraint). A dimension with no options at all is simply not
     * a constraint, so it never wipes the charts.
     */
    private _buildWhere(start: number, end: number): string | null {
        const locTotal = gdbx.allLocations.length;
        const stockTotal = Object.keys(gdbx.stockGroupNames).length;
        const udfTotal = this.udfGroups.length;
        const custTotal = gdbx.allCustomers.length;
        const emptied = (sel: string[], total: number) => total > 0 && sel.length === 0;
        if (emptied(this.selectedLocations, locTotal)
            || emptied(this.selectedStockGroups, stockTotal)
            || emptied(this.selectedUdfGroups, udfTotal)
            || emptied(this.selectedCustomers, custTotal)) {
            return null;
        }

        const esc = (s: string) => s.replace(/'/g, '\'\'');
        // Per dimension: all-selected (or no options) -> no restriction; subset -> IN.
        const filterClause = (col: string, codes: string[], total: number): string | null => {
            if (codes.length >= total) {
                return null;
            }
            return `${col} IN (${codes.map((c) => `'${esc(c)}'`).join(',')})`;
        };

        const clauses: string[] = [
            `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${start} AND ${end}`,
        ];
        const locClause = filterClause(LOCATION_COL, this.selectedLocations, locTotal);
        if (locClause) {
            clauses.push(locClause);
        }
        const stockClause = filterClause(COL.STOCKGROUP, this.selectedStockGroups, stockTotal);
        if (stockClause) {
            clauses.push(stockClause);
        }
        const udfClause = filterClause(UDF_GROUP_COL, this.selectedUdfGroups, udfTotal);
        if (udfClause) {
            clauses.push(udfClause);
        }
        const custClause = filterClause(COL.CODE, this.selectedCustomers, custTotal);
        if (custClause) {
            clauses.push(custClause);
        }
        return clauses.join(' AND ');
    }

    /**
     * SQL expression for the LOCAL amount (home currency). "AMOUNT" is already
     * SUM(LOCALAMOUNT) before tax; Include Tax adds the local "TAX" column via
     * getSqlColumnRef.
     */
    private _amountExpr(includeTax: boolean): string {
        const amountKey = includeTax ? 'amountIncludeTax' : 'amount';
        const amountRef = getSqlColumnRef(CARD, amountKey);
        return isComputedProperty(amountKey)
            ? amountRef
            : `TRY_CAST(${amountRef} AS DOUBLE)`;
    }

    /**
     * Query the single `diySalesIvCsDnCn` table, grouped by the entity column
     * (LOCATION / UDF_GROUP) and by month (or day in 7-Days view), returning the
     * raw IV / CS / DN / CN buckets per entity per period.
     */
    private async _fetchRaw(
        queryHead: any,
        groupCol: string,
        selectedCodes: string[],
        nameMap: { [code: string]: string },
        viewMode: 'month' | '7days',
        includeTax: boolean,
    ): Promise<RawSeries> {
        const { labels, keys } = this._buildPeriods(viewMode);

        const [start, end] = this.displayRange(viewMode);

        // Global filters combine with AND: an emptied filter -> no data on ALL
        // charts (see _buildWhere).
        const where = this._buildWhere(start, end);
        if (where === null) {
            return { labels, keys, entities: [] };
        }

        const amt = this._amountExpr(includeTax);
        const table = getTableName(CARD);

        const isDay = viewMode === '7days';
        const periodCols = isDay
            ? `${sqlExtractYear('"docDate"', 'yr')}, ${sqlExtractMonth('"docDate"', 'mo')}, ${sqlExtractDay('"docDate"', 'dy')}`
            : `${sqlExtractYear('"docDate"', 'yr')}, ${sqlExtractMonth('"docDate"', 'mo')}`;
        const groupBy = isDay ? 'grp, yr, mo, dy' : 'grp, yr, mo';

        // One table, four DOCTYPEs. CN rows are POSITIVE here and are subtracted
        // later in _combine().
        const rows = await queryHead.rawQuery(`
            SELECT grp, ${periodCols},
                SUM(CASE WHEN docType = 'IV' THEN amount ELSE 0 END) as iv,
                SUM(CASE WHEN docType = 'CS' THEN amount ELSE 0 END) as cs,
                SUM(CASE WHEN docType = 'DN' THEN amount ELSE 0 END) as dn,
                SUM(CASE WHEN docType = 'CN' THEN amount ELSE 0 END) as cn
            FROM (
                SELECT ${COL.DOCTYPE} as docType, ${groupCol} as grp,
                    ${amt} as amount, ${COL.DOCDATE} as "docDate"
                FROM ${table}
                WHERE ${where}
            ) AS combined
            GROUP BY ${groupBy}
        `);

        const pad = (n: number) => String(n).padStart(2, '0');
        const keyOf = (r: any) => isDay
            ? `${Number(r.yr)}-${pad(Number(r.mo))}-${pad(Number(r.dy))}`
            : `${Number(r.yr)}-${pad(Number(r.mo))}`;

        const byEntity: { [code: string]: { [k: string]: Cell } } = {};
        for (const r of rows) {
            const code = String(r.grp);
            const pk = keyOf(r);
            if (!byEntity[code]) {
                byEntity[code] = {};
            }
            byEntity[code][pk] = {
                iv: Number(r.iv) || 0,
                cs: Number(r.cs) || 0,
                dn: Number(r.dn) || 0,
                cn: Number(r.cn) || 0,
            };
        }

        // Lines = this chart's selected dimension values that have data, in
        // selection order. When all are selected the WHERE isn't restricted by
        // this dimension, but we still only draw the selected values as lines.
        const entities = selectedCodes
            .filter((code) => byEntity[code])
            .map((code) => ({
                code,
                name: nameMap[code] || code,
                byPeriod: byEntity[code],
            }));

        return { labels, keys, entities };
    }

    private _seriesFromRaw(
        raw: RawSeries | null,
        opts: IncludeOpts,
        showValues: boolean,
    ): any[] {
        if (!raw) {
            return [];
        }
        return raw.entities.map((ent, idx) => {
            const colour = this.palette[idx % this.palette.length];
            return {
                name: ent.name,
                type: 'line',
                smooth: true,
                lineStyle: { color: colour },
                itemStyle: { color: colour },
                label: showValues
                    ? {
                        show: true,
                        position: 'top',
                        color: 'inherit',
                        fontSize: 10,
                        formatter: (p: any) => shortNumber(Number(p.value)),
                    }
                    : { show: false },
                data: raw.keys.map((k) => this._combine(ent.byPeriod[k], opts)),
            };
        });
    }

    /** Sum a location's per-period cells into one total cell (for the pie). */
    private _sumCells(byPeriod: { [k: string]: Cell }): Cell {
        const total: Cell = { iv: 0, cs: 0, dn: 0, cn: 0 };
        for (const k of Object.keys(byPeriod)) {
            const c = byPeriod[k];
            total.iv += c.iv;
            total.cs += c.cs;
            total.dn += c.dn;
            total.cn += c.cn;
        }
        return total;
    }

    /** Net amount from the raw buckets given the include-checkboxes (CN subtracted). */
    private _combine(cell: Cell | undefined, opts: IncludeOpts): number {
        if (!cell) {
            return 0;
        }
        let a = 0;
        if (opts.invoice) {
            a += cell.iv;
        }
        if (opts.cash) {
            a += cell.cs;
        }
        if (opts.debit) {
            a += cell.dn;
        }
        if (opts.credit) {
            a -= cell.cn;
        }
        return Math.round(a * 100) / 100;
    }

    /**
     * Monthly view -> one point per month between "Date From" and "Date To".
     * 7 Days view  -> the 7 days ending at "Date To" ("Date To" - 6 ... "Date To").
     */
    private _buildPeriods(
        viewMode: 'month' | '7days',
    ): { labels: string[]; keys: string[] } {
        const [from, to] = this.selectedDateRange;
        const labels: string[] = [];
        const keys: string[] = [];

        if (viewMode === '7days') {
            const end = moment(to).endOf('day');
            for (let i = 6; i >= 0; i--) {
                const d = moment(end).subtract(i, 'day');
                labels.push(d.format('DD MMM'));
                keys.push(d.format('YYYY-MM-DD'));
            }
            return { labels, keys };
        }

        const cur = moment(from).startOf('month');
        const last = moment(to).startOf('month');
        let guard = 0;
        while (cur.isSameOrBefore(last) && guard < 120) {
            labels.push(cur.format('MMM YYYY'));
            keys.push(cur.format('YYYY-MM'));
            cur.add(1, 'month');
            guard++;
        }
        if (labels.length === 0) {
            labels.push(last.format('MMM YYYY'));
            keys.push(last.format('YYYY-MM'));
        }
        return { labels, keys };
    }

    private _csvCell(value: any): string {
        const s = String(value);
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }
}
